import {
  removeActivityOccurrence,
  useActivityOccurrence,
} from "@/api/Activities";
import { useLingui } from "@lingui/react/macro";
import styles from "./ActivityOccurrenceDetails.module.scss";
import { Heading } from "@medoma/components/Heading/Heading";
import { Text } from "@medoma/components/Text/Text";
import { Loading, LoadingOverlay } from "@medoma/components/Loading/Loading";
import ErrorMessage from "@medoma/components/ErrorMessage/ErrorMessage";
import {
  activityOccurrenceStatusSchema,
  isEmployeePerformed,
  isRoutable,
  timeOfDayDictionary,
  timeOfDaySchema,
  type IActivityOccurrence,
  type IEmployeePerformedCategory,
  type IRoutableCategory,
  type OccurrenceOf,
} from "@medoma/models/activities";
import { patientStatusSchema } from "@medoma/models/patients";
import { formattedTimeSpan } from "@/components/Time/timeUtils";
import { dateName } from "@/Utils/DateUtils";
import { AssignShiftAction } from "@/components/AssignShiftAction/AssignShiftAction";
import { knownFeatureFlagsSchema, useFeatureFlag } from "@/api/FeatureFlags";
import { getUnfulfilledRequirements } from "@medoma/models/activityRequirements";
import { CompetenceChip } from "@/components/Chips/CompetenceChip";
import { deducedError, displayErrorMessageAlert } from "@/Utils/ErrorUtils";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router";
import z from "zod";
import { CategoryIcon } from "@/components/CategoryIcon/CategoryIcon";
import { StatusTag } from "@/components/StatusTag/StatusTag";
import { AssigneesChips } from "@/components/AssigneesChips/AssigneesChips";
import RecurrenceIcon from "@medoma/components/icons/RecurrenceIcon";
import BinIcon from "@medoma/components/icons/BinIcon";
import { IconButton } from "@medoma/components/Button/Button";
import { UnstyledLink } from "@medoma/components/Link/Link";

type IAssignableOccurrence = OccurrenceOf<
  Exclude<IEmployeePerformedCategory, IRoutableCategory>,
  IActivityOccurrence
>;

type IStaffedOccurrence = Extract<IActivityOccurrence, { assignees: unknown }>;

const isAssignableOccurrence = (
  occurrence: IActivityOccurrence,
): occurrence is IAssignableOccurrence =>
  isEmployeePerformed(occurrence) && !isRoutable(occurrence.category);

export const ActivityOccurrenceDetails = () => {
  const { t } = useLingui();
  const { patientId, activityId, occurrenceId } = z
    .object({
      patientId: z.string(),
      activityId: z.string(),
      occurrenceId: z.string(),
    })
    .parse(useParams());
  const {
    data: occurrence,
    isPending,
    isError,
    error,
  } = useActivityOccurrence(activityId, occurrenceId);

  if (isPending) {
    return <Loading message={t`Hämtar aktivitet`} />;
  }

  if (isError) {
    return <ErrorMessage message={deducedError(error)} />;
  }

  return (
    <ActivityOccurrenceSummary occurrence={occurrence} patientId={patientId} />
  );
};

const ActivityOccurrenceSummary = ({
  occurrence,
  patientId,
}: {
  occurrence: IActivityOccurrence;
  patientId: string;
}) => {
  const { t } = useLingui();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: isAssignShiftInGoEnabled } = useFeatureFlag(
    knownFeatureFlagsSchema.enum.AssignShiftInGo,
  );

  const status = occurrence.status;
  const hasNotStarted =
    status !== activityOccurrenceStatusSchema.enum.finished &&
    status !== activityOccurrenceStatusSchema.enum.ongoing;
  const isAnyTimeOfDay = occurrence.timeOfDay === timeOfDaySchema.enum.Any;
  const canManageAssignees = Boolean(
    isAssignableOccurrence(occurrence) &&
    hasNotStarted &&
    isAssignShiftInGoEnabled,
  );

  const { mutate: deleteOccurrence, isPending: isDeletingOccurrence } =
    useMutation({
      mutationFn: () =>
        removeActivityOccurrence(occurrence.activityId, occurrence.id),
      onSuccess: async () => {
        await queryClient.invalidateQueries();
        await navigate(`/patients/${patientId}/activities`);
      },
      onError: (error) => {
        displayErrorMessageAlert(
          `${t`Kunde inte ta bort aktivitet`}: ${deducedError(error)}`,
        );
      },
    });

  const confirmRemove = () => {
    const { title, recurring } = occurrence;
    const message = recurring
      ? t`Är du säker på att du vill ta bort det här aktivitetstillfället för aktiviteten "${title}"?`
      : t`Är du säker på att du vill ta bort aktiviteten "${title}"?`;
    if (window.confirm(message)) {
      deleteOccurrence();
    }
  };

  return (
    <div className={styles.activityOccurrenceDetails}>
      <div className={styles.card}>
        <UnstyledLink
          to="edit"
          aria-label={t`Ändra aktivitet`}
          className={styles.link}
        >
          <div className={styles.header}>
            <CategoryIcon category={occurrence.category} />
            <Heading level="h1" size="h2" weight="regular">
              {occurrence.title}
            </Heading>
            {occurrence.recurring ? <RecurrenceIcon /> : null}
          </div>
          <div className={styles.timeAndDate}>
            <Text element="span" weight="bold" size="large">
              {isAnyTimeOfDay
                ? t(timeOfDayDictionary.Any.short)
                : formattedTimeSpan(occurrence.start, occurrence.end)}
            </Text>
            <Text element="span" weight="regular" size="base">
              {dateName(occurrence.start)}
            </Text>
          </div>
        </UnstyledLink>
        {hasNotStarted ? (
          <div className={styles.trash}>
            <IconButton
              aria-label={t`Ta bort aktivitet`}
              onClick={confirmRemove}
              disabled={isDeletingOccurrence}
            >
              <LoadingOverlay show={isDeletingOccurrence}>
                <BinIcon />
              </LoadingOverlay>
            </IconButton>
          </div>
        ) : null}
        <div className={styles.statusAndAssignees}>
          <StatusTag status={status} variant="icon-and-text" size="small" />
          {isEmployeePerformed(occurrence) ? (
            <AssigneesOrRequirementChips
              occurrence={occurrence}
              canManageAssignees={canManageAssignees}
            />
          ) : null}
        </div>
      </div>

      {isAssignableOccurrence(occurrence) &&
      hasNotStarted &&
      isAssignShiftInGoEnabled ? (
        <AssignShiftForOccurrence occurrence={occurrence} />
      ) : null}
    </div>
  );
};

const AssigneesOrRequirementChips = ({
  occurrence,
  canManageAssignees,
}: {
  occurrence: IStaffedOccurrence;
  canManageAssignees: boolean;
}) => {
  if (occurrence.assignees.length > 0) {
    return (
      <AssigneesChips
        assignees={occurrence.assignees}
        target={{
          kind: "occurrence",
          activityId: occurrence.activityId,
          occurrenceId: occurrence.id,
        }}
        canManageAssignees={canManageAssignees}
      />
    );
  }
  return occurrence.requiredCompetences.map((competence) => (
    <CompetenceChip
      key={competence}
      competence={competence}
      state="unassigned"
    />
  ));
};

const AssignShiftForOccurrence = ({
  occurrence,
}: {
  occurrence: IAssignableOccurrence;
}) => {
  const { patient } = occurrence;

  if (!patient || patient.status === patientStatusSchema.enum.deleted) {
    return null;
  }

  return (
    <AssignShiftAction
      target={{
        kind: "occurrence",
        activityId: occurrence.activityId,
        occurrenceId: occurrence.id,
      }}
      assignedShiftIds={occurrence.assignees.map(({ id }) => id)}
      shiftsFromDate={occurrence.start}
      unfulfilledRequirements={getUnfulfilledRequirements(occurrence)}
      patientSiteId={patient.site.id}
    />
  );
};
